import { Box, CircularProgress, Divider, Typography, Alert } from "@mui/material";
import { East } from "@mui/icons-material";
import { getImprovementColor } from "../utils/uiUtils";
import { ImprovementIcon } from "./ImprovementIcon";

type ComparisonMetricDisplayProps = {
    firstEEGMetricMean: number;
    secondEEGMetricMean: number;
    improvement: number;
    onFirstEEGClick: () => void;
    onSecondEEGClick: () => void;
    variant?: "h4" | "h5" | "h6";
    showComparison: boolean;
    secondEEGIsFetching: boolean;
    rowsMismatchCheck?: {
        first: {
            expectedRows: number;
            actualRows: number;
            hasDataMismatch: boolean;
        };
        second: {
            expectedRows: number;
            actualRows: number;
            hasDataMismatch: boolean;
        };
    };
};

export const ComparisonMetricDisplay = ({
    firstEEGMetricMean,
    secondEEGMetricMean,
    improvement,
    onFirstEEGClick,
    onSecondEEGClick,
    variant = "h4",
    showComparison,
    secondEEGIsFetching,
    rowsMismatchCheck = { first: { expectedRows: 0, actualRows: 0, hasDataMismatch: false }, second: { expectedRows: 0, actualRows: 0, hasDataMismatch: false } },
}: ComparisonMetricDisplayProps) => {
    const improvmentPercentage = (improvement / firstEEGMetricMean) * 100;


    return (
        <>
            <Box display="flex" justifyContent="center" alignItems="center">
                <Typography variant={variant} sx={{ my: 1, cursor: "pointer" }} onClick={onFirstEEGClick}>
                    {firstEEGMetricMean.toFixed(2)}
                </Typography>
                {secondEEGIsFetching ? (
                    <CircularProgress size={20} sx={{ ml: 2 }} />
                ) : (
                    showComparison &&
                    secondEEGMetricMean !== 0 && (
                        <>
                            <East sx={{ fontSize: variant === "h6" ? 18 : 24, mx: 1 }} />
                            <Typography variant={variant} sx={{ my: 1, cursor: "pointer" }} onClick={onSecondEEGClick}>
                                {secondEEGMetricMean.toFixed(2)}
                            </Typography>
                        </>
                    )
                )}
            </Box>

            {rowsMismatchCheck.first.hasDataMismatch && (
                <Alert severity="warning" sx={{ mt: 1, fontSize: variant === "h6" ? "0.75rem" : "0.875rem" }}>
                    Data mismatch: Expected {rowsMismatchCheck.first.expectedRows} rows, but received {rowsMismatchCheck.first.actualRows} for first EEG.
                </Alert>
            )}

            {rowsMismatchCheck.second.hasDataMismatch && showComparison && (
                <Alert severity="warning" sx={{ mt: 1, fontSize: variant === "h6" ? "0.75rem" : "0.875rem" }}>
                    Data mismatch: Expected {rowsMismatchCheck.second.expectedRows} rows, but received {rowsMismatchCheck.second.actualRows} for second EEG.
                </Alert>
            )}

            {showComparison && secondEEGMetricMean !== 0 && (
                <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center">
                    <Box display="flex" alignItems="center" gap={variant === "h6" ? 0.5 : 1}>
                        <ImprovementIcon value={improvement} color={getImprovementColor(improvement)} />
                        <Typography variant={variant === "h6" ? "caption" : "body2"} sx={{ color: getImprovementColor(improvement) }}>
                            {improvement > 0 ? "Melhorou" : improvement < 0 ? "Piorou" : "Não mudou"}
                        </Typography>
                    </Box>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 4, mt: 1 }}>
                        <Typography variant={variant === "h6" ? "body2" : "h5"} sx={{ color: getImprovementColor(improvement) }}>
                            {improvement.toFixed(2)}{" "}
                        </Typography>
                        <Divider orientation="vertical" flexItem />
                        <Typography variant={variant === "h6" ? "body2" : "h5"} sx={{ color: getImprovementColor(improvement) }}>
                            {improvmentPercentage.toFixed(0)}%
                        </Typography>
                    </Box>
                </Box>
            )}
        </>
    );
};
